import { useQuery, UseQueryResult } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

export interface EstimateFactor {
  name: string
  multiplier: number
  reason: string
}

export interface CarrierFacts {
  dotNumber: string
  powerUnits: number | null
  drivers: number | null
  annualMileage: number | null
  yearsInBusiness: number | null
  operationClassification: string | null
  cargoCategories: string[]
  domicileState: string | null
  radiusBucket: string | null
  crashes24mo: number
  fatalCrashes24mo: number
  driverOosRate: number | null
  vehicleOosRate: number | null
  issScore: number | null
}

export interface AutoLiabilityEstimate {
  dotNumber: string
  powerUnits: number
  requestedLimit: number
  perUnitLow: number
  perUnitMid: number
  perUnitHigh: number
  totalLow: number
  totalMid: number
  totalHigh: number
  factors: EstimateFactor[]
  facts: CarrierFacts | null
}

function fetchAutoLiabilityEstimate(dotNumber: string, powerUnits?: number): Promise<AutoLiabilityEstimate> {
  const qs = powerUnits ? `?power_units=${powerUnits}` : ''
  return axios
    .get(`/api/v1/insure/internal/auto-liability-estimate/${dotNumber}/${qs}`)
    .then((response) => toCamelCase(response.data) as AutoLiabilityEstimate)
}

export function useAutoLiabilityEstimate(
  dotNumber: string | undefined,
  powerUnits?: number
): UseQueryResult<AutoLiabilityEstimate, AxiosError<{ detail?: string }>> {
  return useQuery({
    queryKey: [CacheKeys.AUTO_LIABILITY_ESTIMATE, dotNumber, powerUnits],
    queryFn: () => fetchAutoLiabilityEstimate(dotNumber!, powerUnits),
    enabled: !!dotNumber,
    retry: false,
  })
}
